import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Snackbar,
  Alert,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Radio,
  Tooltip,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WebIcon from '@mui/icons-material/Web';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ConstructionIcon from '@mui/icons-material/Construction';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SecurityIcon from '@mui/icons-material/Security';
import KeyIcon from '@mui/icons-material/Key';
import { request } from '../utils/request';
import { BASE_URL } from '../utils/BaseUrl';
import { ColorModeContext } from '../index';
import ReusableTable from '../components/Table/ReusableTable';
import ReusableHeader from '../components/Header/ReusableHeader';
import TableFilterBar from '../components/Table/TableFilterBar';
import { useSearchParams } from 'react-router-dom';
import { hasFeatureAccess, FEATURES } from '../utils/rbac';
import AccessDenied from '../components/guards/AccessDenied';

/* =========================================================
   mEDGE design tokens (mirrors global.css :root values).
   Same variable names as global.css, remapped for dark mode.
   Applied via style={V} on each root node — including MUI
   portals (Dialog/Menu/Snackbar), which render outside this
   component's DOM tree and otherwise wouldn't inherit vars.
   ========================================================= */

const LIGHT_VARS = {
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--canvas': '#FFFFFF',
  '--mist': '#F7F9FC',
  '--hairline': '#E4E9F0',
  '--blue-tint': '#EAF2FE',
  '--slate': '#6B7A90',
  '--secure': '#00A88F',
  '--high': '#E2622B',
  '--elevated': '#F0A202',
  '--critical': '#D6323A',
  '--border-radius': '6px',
};

const DARK_VARS = {
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--canvas': '#0F1729',
  '--mist': '#252b3b',
  '--hairline': 'rgba(255,255,255,0.14)',
  '--blue-tint': 'rgba(76,154,255,0.16)',
  '--slate': '#93A4BD',
  '--secure': '#2FD3B8',
  '--high': '#F07C46',
  '--elevated': '#FFC24B',
  '--critical': '#FF6B72',
  '--border-radius': '6px',
};

const categoryOptions = [
  'Web Application',
  'Desktop Application',
  'Mobile Application',
  'VPN',
  'Windows Login',
  'RDP',
  'Git Repository',
  'FTP',
  'File Server',
  'API',
  'Custom',
];

const statusOptions = ['Active', 'Inactive', 'Testing', 'Deprecated'];
const protocolOptions = ['OIDC', 'SAML', 'LDAP', 'OAuth2', 'RADIUS', 'REST API', 'Custom', 'None'];
const mfaPolicyOptions = ['Enforced', 'Disabled'];

// Merged list: includes both Biometric and Passkey (plus SMS OTP).
const mfaMethodOptions = ['TOTP', 'Push Notification', 'Email OTP', 'SMS OTP', 'Biometric', 'Passkey'];
const fallbackMfaMethodOptions = ['TOTP', 'Email OTP', 'SMS OTP'];

// Helper hints shown under the MFA method list for methods that need extra setup.
const mfaMethodHints = {
  Biometric: 'Biometric MFA requires a trusted mobile device with biometric authentication configured.',
  Passkey: 'Passkey MFA requires a device or security key that supports WebAuthn / FIDO2 passkeys.',
};

const normalizeAllowedMfaMethods = (methods) => {
  if (!Array.isArray(methods)) return [];
  return methods
    .filter((method) => typeof method === 'string' && mfaMethodOptions.includes(method.trim()))
    .map((method) => method.trim());
};

const isValidHttpUrl = (val) => {
  if (!val || typeof val !== 'string') return false;
  try {
    const parsed = new URL(val);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const getBaseOrigin = () => {
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return String(BASE_URL).replace(/\/api\/v1\/?$/, '');
  } catch {
    return 'https://mfa.starsecure.io';
  }
};

const getStoredUser = () => {
  try {
    const raw = typeof window !== 'undefined' ? (localStorage.getItem('user') || sessionStorage.getItem('user')) : null;
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// UUID generator with a fallback for browsers/contexts without crypto.randomUUID
const generateUuid = () => {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex
    .slice(8, 10)
    .join('')}-${hex.slice(10, 16).join('')}`;
};

const emptyForm = {
  id: null,
  applicationName: '',
  applicationCode: '',
  description: '',
  category: 'Web Application',
  applicationType: '',
  status: 'Active',
  applicationUrl: '',
  hostname: '',
  ipAddress: '',
  port: '',
  basePath: '',
  authenticationProtocol: 'OIDC',
  clientId: '',
  clientSecret: '',
  issuer: '',
  redirectUris: '',
  logoutRedirectUris: '',
  metadataUrl: '',
  authorizationEndpoint: '',
  tokenEndpoint: '',
  userInfoEndpoint: '',
  allowedMfaMethods: ['TOTP'],
  defaultMfaMethod: '',
  mfaRequired: false,
  allowedDomains: '',
  allowedIPs: '',
  allowedNetworks: '',
  sessionTimeoutMinutes: '',
  allowRememberDevice: false,
  rememberDeviceDays: '',
  maxConcurrentSessions: '',
  ownerDepartment: '',
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
  companyId: '',
  saml: {
    spEntityId: '',
    acsUrls: '',
    defaultAcsUrl: '',
    nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
    nameIdAttribute: 'email',
    signAssertion: true,
    signResponse: false,
    wantAuthnRequestsSigned: false,
    spCertificate: '',
    assertionLifetimeSeconds: 300,
    maxGroupCount: 50,
    rawSpMetadataXml: '',
  },
};

const protocolChipClass = (protocol) => {
  switch (protocol) {
    case 'SAML':
      return 'global-chip signal-secure-bg';
    case 'OIDC':
      return 'global-chip signal-elevated-bg';
    default:
      return 'global-chip';
  }
};

export default function ApplicationsPage() {
  const user = getStoredUser();
  const canRead = hasFeatureAccess(user, FEATURES.APPLICATIONS, 'read');
  const canCreate = hasFeatureAccess(user, FEATURES.APPLICATIONS, 'create');
  const canUpdate = hasFeatureAccess(user, FEATURES.APPLICATIONS, 'update');
  const canDelete = hasFeatureAccess(user, FEATURES.APPLICATIONS, 'delete');

  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialFilters = () => {
    const mfaParam = searchParams.get('mfaRequired');
    let mfaVal = '';
    if (mfaParam === 'false' || mfaParam === 'disabled' || mfaParam === 'off') {
      mfaVal = 'Disabled';
    } else if (mfaParam === 'true' || mfaParam === 'enforced' || mfaParam === 'enabled') {
      mfaVal = 'Enforced';
    }
    return {
      status: searchParams.get('status') || '',
      category: searchParams.get('category') || '',
      authenticationProtocol: searchParams.get('authenticationProtocol') || '',
      search: searchParams.get('search') || '',
      mfaRequired: mfaVal,
    };
  };

  const [applications, setApplications] = useState([]);
  const [open, setOpen] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [viewData, setViewData] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingApplication, setDeletingApplication] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const [filters, setFilters] = useState(getInitialFilters);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Basic Info', 'Connection', 'Authentication', 'MFA Policy', 'Security', 'Ownership'];

  // Critical modification warnings
  const [originalProtocol, setOriginalProtocol] = useState('OIDC');
  const [originalSamlValues, setOriginalSamlValues] = useState({ spEntityId: '', defaultAcsUrl: '', acsUrls: '' });

  // SP Metadata import state
  const [importTab, setImportTab] = useState(0);
  const [importUrl, setImportUrl] = useState('');
  const [importXml, setImportXml] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  // IdP Details & Certificate Management
  const [idpInfo, setIdpInfo] = useState(null);
  const [stageConfirmOpen, setStageConfirmOpen] = useState(false);
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false);
  const [certActionLoading, setCertActionLoading] = useState(false);

  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const V = mode === 'dark' ? DARK_VARS : LIGHT_VARS;

  const currentUser = getStoredUser();
  const isSuperAdmin = Boolean(currentUser?.superadmin || currentUser?.role === 'SuperAdmin');

  // Shared "notch cutout" style for shrunk field labels
  const fieldLabelSx = { backgroundColor: 'var(--canvas)', px: 0.5, color: 'var(--slate)' };

  // Select popover menu
  const selectMenuProps = {
    PaperProps: {
      className: 'global-card',
      style: V,
      sx: {
        mt: 0.5,
        '& .MuiList-root': { padding: 0 },
        '& .MuiMenuItem-root': {
          fontFamily: 'Calibri, Arial, sans-serif',
          fontSize: 17,
          color: 'var(--vault-navy)',
        },
        '& .MuiMenuItem-root[data-value=""]': {
          backgroundColor: 'var(--mist)',
          borderBottom: '1px solid var(--hairline)',
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: 'var(--mist)',
        },
        '& .Mui-selected': {
          backgroundColor: 'var(--blue-tint) !important',
        },
      },
    },
  };

  useEffect(() => {
    const mfaParam = searchParams.get('mfaRequired');
    const resolvedMfa =
      mfaParam === 'false' || mfaParam === 'disabled' || mfaParam === 'off'
        ? 'Disabled'
        : mfaParam === 'true' || mfaParam === 'enforced' || mfaParam === 'enabled'
          ? 'Enforced'
          : '';
    setFilters((prev) => {
      const nextStatus = searchParams.get('status') || '';
      const nextCategory = searchParams.get('category') || '';
      const nextProtocol = searchParams.get('authenticationProtocol') || '';
      const nextSearch = searchParams.get('search') || '';

      if (
        (mfaParam !== null && prev.mfaRequired !== resolvedMfa) ||
        (searchParams.get('status') !== null && prev.status !== nextStatus) ||
        (searchParams.get('category') !== null && prev.category !== nextCategory) ||
        (searchParams.get('authenticationProtocol') !== null && prev.authenticationProtocol !== nextProtocol) ||
        (searchParams.get('search') !== null && prev.search !== nextSearch)
      ) {
        return {
          ...prev,
          mfaRequired: mfaParam !== null ? resolvedMfa : prev.mfaRequired,
          status: searchParams.get('status') !== null ? nextStatus : prev.status,
          category: searchParams.get('category') !== null ? nextCategory : prev.category,
          authenticationProtocol: searchParams.get('authenticationProtocol') !== null ? nextProtocol : prev.authenticationProtocol,
          search: searchParams.get('search') !== null ? nextSearch : prev.search,
        };
      }
      return prev;
    });
  }, [searchParams]);

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.category, filters.authenticationProtocol, filters.mfaRequired, pagination.page, pagination.limit]);

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const copyToClipboard = async (text, label) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showSnack(`Copied ${label} to clipboard!`, 'success');
    } catch {
      showSnack(`Failed to copy ${label}.`, 'error');
    }
  };

  const handleCopy = (text, name) => {
    if (!text) {
      showSnack(`No ${name} to copy`, 'error');
      return;
    }
    copyToClipboard(text, name);
  };

  const handleGenerateClientId = () => {
    setFormData((prev) => ({ ...prev, clientId: generateUuid() }));
  };

  const handleGenerateClientSecret = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const secret = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    setFormData((prev) => ({ ...prev, clientSecret: secret }));
  };

  const fetchApplications = async () => {
    if (!canRead) return;
    try {
      const query = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        status: filters.status || undefined,
        category: filters.category || undefined,
        authenticationProtocol: filters.authenticationProtocol || undefined,
        mfaRequired:
          filters.mfaRequired === 'Enforced'
            ? 'true'
            : filters.mfaRequired === 'Disabled'
              ? 'false'
              : undefined,
      };
      const response = await request('applications', { method: 'get', query });
      let data = Array.isArray(response?.data) ? response.data : response?.data || [];

      // Fallback: in case backend is remote/undeployed and doesn't filter by mfaRequired yet
      if (filters.mfaRequired === 'Disabled') {
        data = data.filter((item) => !item.mfaRequired);
      } else if (filters.mfaRequired === 'Enforced') {
        data = data.filter((item) => Boolean(item.mfaRequired));
      }
      if (filters.status) {
        data = data.filter((item) => item.status === filters.status);
      }

      setApplications(data);
      if (response?.pagination) {
        setPagination((prev) => ({
          ...prev,
          ...response.pagination,
          total: filters.mfaRequired ? data.length : response.pagination.total,
        }));
      }
    } catch (err) {
      setApplications([]);
      showSnack('Failed to load applications.', 'error');
    }
  };

  const fetchIdpInfo = async () => {
    try {
      const res = await request('applications/idp-info', { method: 'get' });
      if (res?.data) {
        setIdpInfo(res.data);
      }
    } catch (err) {
      // Non-fatal if IdP info fails to load
    }
  };

  const handleOpen = (application) => {
    setImportError('');
    setImportSuccess('');
    setImportUrl('');
    setImportXml('');
    setImportFile(null);
    setShowClientSecret(false);

    if (application) {
      const samlData = application.saml || {};
      const samlAcsUrls = Array.isArray(samlData.acsUrls)
        ? samlData.acsUrls.join(', ')
        : (typeof samlData.acsUrls === 'string' ? samlData.acsUrls : '');

      setFormData({
        ...emptyForm,
        ...application,
        id: application._id || application.id,
        companyId: application.companyId || '',
        redirectUris: Array.isArray(application.redirectUris) ? application.redirectUris.join(', ') : '',
        logoutRedirectUris: Array.isArray(application.logoutRedirectUris) ? application.logoutRedirectUris.join(', ') : '',
        allowedMfaMethods: normalizeAllowedMfaMethods(application.allowedMfaMethods).slice(0, 1),
        allowedDomains: Array.isArray(application.allowedDomains) ? application.allowedDomains.join(', ') : '',
        allowedIPs: Array.isArray(application.allowedIPs) ? application.allowedIPs.join(', ') : '',
        allowedNetworks: Array.isArray(application.allowedNetworks) ? application.allowedNetworks.join(', ') : '',
        saml: {
          spEntityId: samlData.spEntityId || '',
          acsUrls: samlAcsUrls,
          defaultAcsUrl: samlData.defaultAcsUrl || '',
          nameIdFormat: samlData.nameIdFormat || 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
          nameIdAttribute: samlData.nameIdAttribute || 'email',
          signAssertion: samlData.signAssertion !== undefined ? Boolean(samlData.signAssertion) : true,
          signResponse: samlData.signResponse !== undefined ? Boolean(samlData.signResponse) : false,
          wantAuthnRequestsSigned: Boolean(samlData.wantAuthnRequestsSigned),
          spCertificate: samlData.spCertificate || '',
          assertionLifetimeSeconds: samlData.assertionLifetimeSeconds || 300,
          maxGroupCount: samlData.maxGroupCount || 50,
          rawSpMetadataXml: samlData.rawSpMetadataXml || '',
        },
      });
      setOriginalProtocol(application.authenticationProtocol || 'OIDC');
      setOriginalSamlValues({
        spEntityId: samlData.spEntityId || '',
        defaultAcsUrl: samlData.defaultAcsUrl || '',
        acsUrls: samlAcsUrls,
      });
      setIsEditing(true);
    } else {
      setFormData(emptyForm);
      setOriginalProtocol('OIDC');
      setOriginalSamlValues({ spEntityId: '', defaultAcsUrl: '', acsUrls: '' });
      setIsEditing(false);
    }
    setActiveStep(0);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleView = async (application) => {
    setViewData(application);
    setOpenView(true);
    if (application.authenticationProtocol === 'SAML') {
      await fetchIdpInfo();
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'mfaRequired' || name === 'allowRememberDevice' ? checked : value,
    }));
  };

  const handleSamlChange = (e) => {
    const { name, value, checked, type } = e.target;
    const resolvedValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({
      ...prev,
      saml: {
        ...prev.saml,
        [name]: resolvedValue,
      },
    }));
  };

  const selectAllowedMfaMethod = (method) => {
    setFormData((prev) => ({
      ...prev,
      allowedMfaMethods: [method],
      defaultMfaMethod: prev.defaultMfaMethod === method ? '' : prev.defaultMfaMethod,
    }));
  };

  // Import SP Metadata Handler
  const handleImportSpMetadata = async () => {
    setImportError('');
    setImportSuccess('');
    setImportLoading(true);

    try {
      let body = {};
      if (importTab === 0) {
        if (!importUrl || !importUrl.trim()) {
          throw new Error('Please enter a valid HTTPS SP metadata URL.');
        }
        body = { url: importUrl.trim() };
      } else if (importTab === 1) {
        if (!importFile) {
          throw new Error('Please select an XML metadata file to upload.');
        }
        const fileContent = await importFile.text();
        body = { xml: fileContent };
      } else {
        if (!importXml || !importXml.trim()) {
          throw new Error('Please paste SP metadata XML content.');
        }
        body = { xml: importXml.trim() };
      }

      const res = await request('applications/import-sp-metadata', {
        method: 'post',
        body,
      });

      if (!res?.success || !res?.data) {
        throw new Error(res?.message || 'Failed to parse SP metadata.');
      }

      const data = res.data;
      const acsUrlsStr = Array.isArray(data.acsUrls) ? data.acsUrls.join(', ') : (data.acsUrls || '');

      setFormData((prev) => ({
        ...prev,
        applicationName: prev.applicationName || (data.spEntityId ? data.spEntityId.replace(/^https?:\/\//, '').split('/')[0] : ''),
        applicationCode: prev.applicationCode || (data.spEntityId ? data.spEntityId.replace(/[^a-zA-Z0-9_-]/g, '-').slice(-30) : ''),
        saml: {
          ...prev.saml,
          spEntityId: data.spEntityId || prev.saml.spEntityId,
          acsUrls: acsUrlsStr || prev.saml.acsUrls,
          defaultAcsUrl: data.defaultAcsUrl || (data.acsUrls && data.acsUrls[0]) || prev.saml.defaultAcsUrl,
          nameIdFormat: data.nameIdFormat || prev.saml.nameIdFormat,
          wantAuthnRequestsSigned: Boolean(data.wantAuthnRequestsSigned),
          spCertificate: data.spCertificate || prev.saml.spCertificate,
          rawSpMetadataXml: data.rawXml || prev.saml.rawSpMetadataXml,
        },
      }));

      setImportSuccess('SP metadata imported successfully! You can review and edit the auto-filled fields below.');
    } catch (err) {
      const errMsg = err?.message || err?.error || (typeof err === 'string' ? err : 'Metadata import failed.');
      setImportError(errMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadMetadata = () => {
    try {
      const origin = getBaseOrigin();
      const metaUrl = idpInfo?.metadataUrl || `${origin}/saml/metadata`;
      window.open(metaUrl, '_blank');
    } catch (err) {
      showSnack('Failed to open metadata: ' + (err?.message || err), 'error');
    }
  };

  // Certificate Management: Stage 'next' certificate
  const handleStageCert = async () => {
    setCertActionLoading(true);
    try {
      const res = await request('applications/rotate-idp-cert', { method: 'post' });
      setStageConfirmOpen(false);
      showSnack(res?.message || 'IdP certificate successfully staged as "next".', 'success');
      await fetchIdpInfo();
    } catch (err) {
      showSnack(err?.message || 'Failed to stage new certificate.', 'error');
    } finally {
      setCertActionLoading(false);
    }
  };

  // Certificate Management: Promote staged certificate
  const handlePromoteCert = async () => {
    setCertActionLoading(true);
    try {
      const res = await request('applications/promote-idp-cert', { method: 'post' });
      setPromoteConfirmOpen(false);
      showSnack(res?.message || 'Staged certificate promoted to active signing key.', 'success');
      await fetchIdpInfo();
    } catch (err) {
      showSnack(err?.message || 'Failed to promote certificate.', 'error');
    } finally {
      setCertActionLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // 1. Basic validation
      if (!formData.applicationName || !formData.applicationName.trim()) {
        showSnack('Application Name is required.', 'error');
        setActiveStep(0);
        return;
      }
      if (!formData.applicationCode || !formData.applicationCode.trim()) {
        showSnack('Application Code is required.', 'error');
        setActiveStep(0);
        return;
      }

      // 2. Strict MFA validation
      const allowedMfaMethods = normalizeAllowedMfaMethods(formData.allowedMfaMethods);
      const defaultMfaMethod = typeof formData.defaultMfaMethod === 'string' ? formData.defaultMfaMethod.trim() : '';

      if (allowedMfaMethods.length !== 1) {
        showSnack('Exactly one MFA policy method must be selected.', 'error');
        setActiveStep(3);
        return;
      }

      if (defaultMfaMethod && defaultMfaMethod === allowedMfaMethods[0]) {
        showSnack('Fallback MFA method must differ from the allowed MFA policy method.', 'error');
        setActiveStep(3);
        return;
      }

      const payload = {
        ...formData,
        applicationName: formData.applicationName.trim(),
        applicationCode: formData.applicationCode.trim(),
        allowedMfaMethods,
        defaultMfaMethod: defaultMfaMethod || '',
        port: formData.port ? Number(formData.port) : undefined,
        sessionTimeoutMinutes: formData.sessionTimeoutMinutes ? Number(formData.sessionTimeoutMinutes) : undefined,
        rememberDeviceDays: formData.rememberDeviceDays ? Number(formData.rememberDeviceDays) : undefined,
        maxConcurrentSessions: formData.maxConcurrentSessions ? Number(formData.maxConcurrentSessions) : undefined,
        companyId: formData.companyId || undefined,
      };

      // 3. Protocol-specific validation & payload shaping
      if (formData.authenticationProtocol === 'SAML') {
        const saml = formData.saml || {};
        const spEntityId = (saml.spEntityId || '').trim();
        if (!spEntityId) {
          showSnack('SP Entity ID is required for SAML applications.', 'error');
          setActiveStep(2);
          return;
        }

        const acsList = (saml.acsUrls || '')
          .split(/[,\n]/)
          .map((u) => u.trim())
          .filter(Boolean);

        if (acsList.length === 0) {
          showSnack('At least one valid ACS URL is required for SAML applications.', 'error');
          setActiveStep(2);
          return;
        }

        for (const u of acsList) {
          if (!isValidHttpUrl(u)) {
            showSnack(`Invalid ACS URL: "${u}". All ACS endpoints must be valid HTTP/HTTPS URLs.`, 'error');
            setActiveStep(2);
            return;
          }
        }

        const defaultAcsUrl = (saml.defaultAcsUrl || '').trim() || acsList[0];
        if (!acsList.includes(defaultAcsUrl)) {
          showSnack('Default ACS URL must be one of the configured ACS URLs.', 'error');
          setActiveStep(2);
          return;
        }

        if (!saml.signAssertion && !saml.signResponse) {
          showSnack('At least one of "Sign Assertion" or "Sign Response" must be enabled.', 'error');
          setActiveStep(2);
          return;
        }

        if (saml.wantAuthnRequestsSigned && (!saml.spCertificate || !saml.spCertificate.trim())) {
          showSnack('SP Certificate is required when "Enforce Signed AuthnRequests" is enabled.', 'error');
          setActiveStep(2);
          return;
        }

        if (saml.spCertificate && saml.spCertificate.trim()) {
          const cert = saml.spCertificate.trim();
          if (!cert.includes('BEGIN CERTIFICATE') || !cert.includes('END CERTIFICATE')) {
            showSnack('SP Certificate must be a valid PEM formatted certificate containing BEGIN/END CERTIFICATE.', 'error');
            setActiveStep(2);
            return;
          }
        }

        payload.saml = {
          spEntityId,
          acsUrls: acsList,
          defaultAcsUrl,
          nameIdFormat: saml.nameIdFormat,
          nameIdAttribute: saml.nameIdAttribute,
          signAssertion: Boolean(saml.signAssertion),
          signResponse: Boolean(saml.signResponse),
          wantAuthnRequestsSigned: Boolean(saml.wantAuthnRequestsSigned),
          spCertificate: saml.spCertificate ? saml.spCertificate.trim() : null,
          assertionLifetimeSeconds: Number(saml.assertionLifetimeSeconds) || 300,
          maxGroupCount: Number(saml.maxGroupCount) || 50,
          rawSpMetadataXml: saml.rawSpMetadataXml || null,
        };
      } else {
        // OIDC / other protocols
        payload.redirectUris = (formData.redirectUris || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        payload.logoutRedirectUris = (formData.logoutRedirectUris || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        payload.allowedDomains = (formData.allowedDomains || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        payload.allowedIPs = (formData.allowedIPs || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        payload.allowedNetworks = (formData.allowedNetworks || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      const opts = isEditing
        ? { method: 'put', params: formData.id, body: payload }
        : { method: 'post', body: payload };

      await request('applications', opts);
      await fetchApplications();
      handleClose();
      showSnack(isEditing ? 'Application updated successfully.' : 'Application created successfully.', 'success');
    } catch (err) {
      let errorMessage = 'Failed to save application.';
      if (err?.errors && typeof err.errors === 'object') {
        errorMessage = Object.values(err.errors).join(', ');
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      showSnack(errorMessage, 'error');
    }
  };

  const handleDeleteClick = (application) => {
    setDeletingApplication(application);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingApplication(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingApplication || !deletingApplication._id) return;
    setDeleting(true);
    try {
      await request('applications', { method: 'delete', params: deletingApplication._id });
      await fetchApplications();
      showSnack(`Application "${deletingApplication.applicationName}" successfully deleted.`, 'success');
      handleCancelDelete();
    } catch (err) {
      showSnack('Failed to delete application.', 'error');
      setDeleting(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));

    // Keep URL search params updated
    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);
      if (name === 'mfaRequired') {
        if (value === 'Disabled') nextParams.set('mfaRequired', 'false');
        else if (value === 'Enforced') nextParams.set('mfaRequired', 'true');
        else nextParams.delete('mfaRequired');
      } else if (value) {
        nextParams.set(name, value);
      } else {
        nextParams.delete(name);
      }
      return nextParams;
    }, { replace: true });
  };

  const selectedMfaMethod = formData.allowedMfaMethods[0];
  const activeCert = idpInfo?.certificates?.find((c) => c.status === 'active');
  const nextCert = idpInfo?.certificates?.find((c) => c.status === 'next');

  if (!canRead) {
    return <AccessDenied featureName="Applications" title="Applications" />;
  }

  return (
    <Box p={2} style={V}>

      <ReusableHeader
        title="Applications"
        breadcrumbs={[{ label: 'Applications' }]}
        actionButton={
          canCreate ? (
            <Button
              size="small"
              variant="contained"
              onClick={() => handleOpen()}
              className="global-button global-button-primary"
              sx={{ mb: 0.3 }}
            >
              Add Application
            </Button>
          ) : null
        }
      />

      <Box sx={{ mt: 3 }}>
        <TableFilterBar
          searchQuery={filters.search}
          onSearchChange={(val) => handleFilterChange({ target: { name: 'search', value: val } })}
          searchPlaceholder="Search Applications..."
          filters={[
            { id: 'status', label: 'Status', options: statusOptions, value: filters.status },
            { id: 'category', label: 'Category', options: categoryOptions, value: filters.category },
            { id: 'authenticationProtocol', label: 'Protocol', options: protocolOptions, value: filters.authenticationProtocol },
            { id: 'mfaRequired', label: 'MFA Policy', options: mfaPolicyOptions, value: filters.mfaRequired },
          ]}
          onFilterChange={(id, value) => handleFilterChange({ target: { name: id, value: value } })}
        />

        <ReusableTable
          columns={[
            {
              id: 'applicationName',
              label: 'Application Name',
              render: (row) => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--blue-tint)', color: 'var(--edge-blue)' }}>
                    {row.category === 'Desktop Application' ? <DesktopWindowsIcon fontSize="small" /> : row.category === 'Mobile Application' ? <PhoneIphoneIcon fontSize="small" /> : <WebIcon fontSize="small" />}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--vault-navy)' }}>
                    {row.applicationName || '—'}
                  </Typography>
                </Stack>
              ),
            },
            { id: 'applicationCode', label: 'Code' },
            { id: 'category', label: 'Category', render: (row) => row.category || '—' },
            {
              id: 'authenticationProtocol',
              label: 'Protocol',
              render: (row) => row.authenticationProtocol ? (
                <Typography variant="body2" sx={{ color: 'var(--edge-blue)', fontWeight: 600 }}>{row.authenticationProtocol}</Typography>
              ) : '—',
            },
            { id: 'defaultMfaMethod', label: 'Fallback MFA', render: (row) => row.defaultMfaMethod || '—' },
            {
              id: 'status',
              label: 'Status',
              render: (row) => {
                const isActive = row.status === 'Active';
                const isTesting = row.status === 'Testing';
                const isDeprecated = row.status === 'Deprecated';
                const color = isActive ? 'var(--secure)' : isTesting ? 'var(--elevated)' : isDeprecated ? 'var(--critical)' : 'var(--slate)';
                const Icon = isActive ? CheckCircleOutlineIcon : isTesting ? ConstructionIcon : isDeprecated ? WarningAmberIcon : CancelOutlinedIcon;

                return (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color }}>
                    <Icon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.status || 'Active'}
                    </Typography>
                  </Stack>
                );
              },
            },
            {
              id: 'mfaRequired',
              label: 'MFA Policy',
              render: (row) => {
                const isEnforced = Boolean(row.mfaRequired);
                return (
                  <Chip
                    size="small"
                    label={isEnforced ? 'Enforced' : 'Disabled'}
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      backgroundColor: isEnforced ? 'rgba(0, 168, 143, 0.12)' : 'rgba(226, 98, 43, 0.12)',
                      color: isEnforced ? 'var(--secure)' : 'var(--high)',
                      border: `1px solid ${isEnforced ? 'rgba(0, 168, 143, 0.3)' : 'rgba(226, 98, 43, 0.3)'}`,
                      height: 22,
                    }}
                  />
                );
              },
            },
            {
              id: 'allowedMfaMethods',
              label: 'Allowed MFA Method',
              render: (row) => Array.isArray(row.allowedMfaMethods) && row.allowedMfaMethods.length > 0 ? (
                <Typography variant="body2" sx={{ color: 'var(--edge-blue)', fontWeight: 500 }}>{row.allowedMfaMethods[0]}</Typography>
              ) : '—',
            },
            {
              id: 'actions',
              label: 'Actions',
              align: 'center',
              minWidth: '120px',
              render: (row) => (
                <Box className="table-actions" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                  <IconButton size="small" onClick={() => handleView(row)} className="global-icon-button" title="View Application">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  {canUpdate && (
                    <IconButton size="small" onClick={() => handleOpen(row)} className="global-icon-button global-icon-button-edit" title="Edit Application">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton size="small" onClick={() => handleDeleteClick(row)} className="global-icon-button global-icon-button-delete" title="Delete Application">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ),
            },
          ]}
          data={applications}
          loading={false}
          page={pagination.page - 1}
          rowsPerPage={pagination.limit}
          total={pagination.total}
          onPageChange={(_, nextPage) => setPagination((prev) => ({ ...prev, page: nextPage + 1 }))}
          onRowsPerPageChange={(event) => setPagination((prev) => ({ ...prev, limit: Number(event.target.value), page: 1 }))}
          emptyMessage="No applications found."
        />
      </Box>

      {/* Add / Edit dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperProps={{ className: 'global-card', style: V }}>
        <DialogTitle className="global-dialog-title">
          {isEditing ? 'Edit Application' : 'Add Application'}
        </DialogTitle>
        <DialogContent sx={{ p: 2, borderColor: 'var(--hairline)' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': { color: 'var(--edge-blue)' },
                      '&.Mui-completed': { color: 'var(--vault-navy)' },
                    },
                  }}
                >
                  <Typography sx={{ color: 'var(--vault-navy)', fontWeight: 500 }}>{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack spacing={3} mt={1}>
            {/* Warning: Protocol change on existing app */}
            {isEditing && originalProtocol && originalProtocol !== formData.authenticationProtocol && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1 }}>
                <strong>Warning: Protocol Change Detected.</strong> You are changing the authentication protocol from{' '}
                <strong>{originalProtocol}</strong> to <strong>{formData.authenticationProtocol}</strong>. Saved configuration for
                both protocols is preserved in form state, but changing the protocol in production alters how users authenticate and
                will break active logins until reconfigured.
              </Alert>
            )}

            {/* Warning: Critical SAML EntityID / ACS modification on existing app */}
            {isEditing && formData.authenticationProtocol === 'SAML' && originalProtocol === 'SAML' && (
              formData.saml.spEntityId !== originalSamlValues.spEntityId ||
              formData.saml.defaultAcsUrl !== originalSamlValues.defaultAcsUrl ||
              formData.saml.acsUrls !== originalSamlValues.acsUrls
            ) && (
              <Alert severity="warning" icon={<WarningAmberIcon />}>
                <strong>Warning: Critical SAML Settings Modified.</strong> You modified the SP Entity ID or ACS URL of an active SAML
                application. The remote Service Provider's configuration MUST be updated to match these new values; otherwise SAML Single
                Sign-On will fail.
              </Alert>
            )}

            {/* STEP 0: BASIC INFO */}
            {activeStep === 0 && (
              <Box>
                <Typography variant="subtitle1" className="global-h3">Basic Information</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={1}>
                  <TextField size="small" label="Application Name" name="applicationName" value={formData.applicationName} onChange={handleChange} fullWidth required className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Application Code" name="applicationCode" value={formData.applicationCode} onChange={handleChange} fullWidth required className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
                  <TextField size="small" label="Description" name="description" value={formData.description} onChange={handleChange} fullWidth multiline rows={2} className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <FormControl size="small" fullWidth className="global-input global-form-field">
                    <InputLabel shrink sx={fieldLabelSx}>Category</InputLabel>
                    <Select name="category" value={formData.category} label="Category" onChange={handleChange} MenuProps={selectMenuProps} notched>
                      <MenuItem value="">None</MenuItem>
                      {categoryOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
                  <TextField size="small" label="Application Type" name="applicationType" value={formData.applicationType} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <FormControl size="small" fullWidth className="global-input global-form-field">
                    <InputLabel shrink sx={fieldLabelSx}>Status</InputLabel>
                    <Select name="status" value={formData.status} label="Status" onChange={handleChange} MenuProps={selectMenuProps} notched>
                      {statusOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            )}

            {/* STEP 1: CONNECTION */}
            {activeStep === 1 && (
              <Box>
                <Typography variant="subtitle1" className="global-h3">Connection</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={1}>
                  <TextField size="small" label="Application URL" name="applicationUrl" value={formData.applicationUrl} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Hostname" name="hostname" value={formData.hostname} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
                  <TextField size="small" label="IP Address" name="ipAddress" value={formData.ipAddress} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Port" name="port" type="number" value={formData.port} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Base Path" name="basePath" value={formData.basePath} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
              </Box>
            )}

            {/* STEP 2: AUTHENTICATION */}
            {activeStep === 2 && (
              <Box>
                <Typography variant="subtitle1" className="global-h3">Authentication</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={1} mb={2}>
                  <FormControl size="small" fullWidth className="global-input global-form-field">
                    <InputLabel id="auth-protocol-label" shrink sx={fieldLabelSx}>Authentication Protocol</InputLabel>
                    <Select
                      labelId="auth-protocol-label"
                      id="auth-protocol-select"
                      name="authenticationProtocol"
                      value={formData.authenticationProtocol}
                      label="Authentication Protocol"
                      onChange={handleChange}
                      MenuProps={selectMenuProps}
                      notched
                    >
                      {protocolOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>

                {/* SAML SECTION */}
                {formData.authenticationProtocol === 'SAML' && (
                  <Stack spacing={2.5}>
                    {/* Import SP Metadata Sub-panel */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderColor: 'var(--hairline)',
                        backgroundColor: 'var(--mist)',
                        borderRadius: 'var(--border-radius)',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <CloudUploadIcon sx={{ color: 'var(--edge-blue)', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--vault-navy)' }}>
                          Import Service Provider (SP) Metadata
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: 'var(--slate)', display: 'block', mb: 1.5 }}>
                        Auto-fill SP Entity ID, ACS endpoints, and certificates from a remote URL, local XML file, or raw XML string.
                      </Typography>

                      <Tabs
                        value={importTab}
                        onChange={(e, val) => { setImportTab(val); setImportError(''); setImportSuccess(''); }}
                        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 14 } }}
                      >
                        <Tab label="Remote URL" />
                        <Tab label="Upload XML File" />
                        <Tab label="Paste XML Content" />
                      </Tabs>

                      {importTab === 0 && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                          <TextField
                            size="small"
                            label="Metadata URL"
                            placeholder="https://samltest.id/saml/sp"
                            value={importUrl}
                            onChange={(e) => setImportUrl(e.target.value)}
                            fullWidth
                            className="global-input global-form-field"
                            InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                          />
                          <Button
                            variant="contained"
                            onClick={handleImportSpMetadata}
                            disabled={importLoading || !importUrl.trim()}
                            className="global-button global-button-primary"
                            sx={{ whiteSpace: 'nowrap', minWidth: 150 }}
                          >
                            {importLoading ? <CircularProgress size={20} color="inherit" /> : 'Fetch & Import'}
                          </Button>
                        </Stack>
                      )}

                      {importTab === 1 && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            className="global-button global-button-secondary"
                          >
                            {importFile ? importFile.name : 'Choose XML File'}
                            <input
                              type="file"
                              hidden
                              accept=".xml,text/xml,application/xml"
                              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                            />
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleImportSpMetadata}
                            disabled={importLoading || !importFile}
                            className="global-button global-button-primary"
                            sx={{ whiteSpace: 'nowrap', minWidth: 150 }}
                          >
                            {importLoading ? <CircularProgress size={20} color="inherit" /> : 'Parse & Import'}
                          </Button>
                        </Stack>
                      )}

                      {importTab === 2 && (
                        <Stack spacing={1.5}>
                          <TextField
                            size="small"
                            label="Paste SP Metadata XML"
                            value={importXml}
                            onChange={(e) => setImportXml(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="<md:EntityDescriptor xmlns:md='urn:oasis:names:tc:SAML:2.0:metadata'..."
                            className="global-input global-form-field"
                            InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                          />
                          <Box>
                            <Button
                              variant="contained"
                              onClick={handleImportSpMetadata}
                              disabled={importLoading || !importXml.trim()}
                              className="global-button global-button-primary"
                              sx={{ minWidth: 150 }}
                            >
                              {importLoading ? <CircularProgress size={20} color="inherit" /> : 'Parse & Import'}
                            </Button>
                          </Box>
                        </Stack>
                      )}

                      {importError && (
                        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setImportError('')}>
                          {importError}
                        </Alert>
                      )}
                      {importSuccess && (
                        <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setImportSuccess('')}>
                          {importSuccess}
                        </Alert>
                      )}
                    </Paper>

                    {/* SAML Fields */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        size="small"
                        label="SP Entity ID"
                        name="spEntityId"
                        value={formData.saml.spEntityId}
                        onChange={handleSamlChange}
                        fullWidth
                        required
                        helperText="Canonical entityID identifying the Service Provider (e.g. https://samltest.id/saml/sp)"
                        className="global-input global-form-field"
                        InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                      />
                      <FormControl size="small" fullWidth className="global-input global-form-field">
                        <InputLabel shrink sx={fieldLabelSx}>NameID Format</InputLabel>
                        <Select
                          name="nameIdFormat"
                          value={formData.saml.nameIdFormat}
                          label="NameID Format"
                          onChange={handleSamlChange}
                          MenuProps={selectMenuProps}
                          notched
                        >
                          <MenuItem value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">
                            Email Address (urn:...:emailAddress)
                          </MenuItem>
                          <MenuItem value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">
                            Persistent (urn:...:persistent)
                          </MenuItem>
                          <MenuItem value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">
                            Transient (urn:...:transient)
                          </MenuItem>
                          <MenuItem value="urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified">
                            Unspecified (urn:...:unspecified)
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        size="small"
                        label="ACS URLs (Assertion Consumer Service)"
                        name="acsUrls"
                        value={formData.saml.acsUrls}
                        onChange={handleSamlChange}
                        fullWidth
                        required
                        multiline
                        rows={2}
                        helperText="Valid HTTP/HTTPS ACS endpoints, comma or newline separated"
                        className="global-input global-form-field"
                        InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                      />
                      <TextField
                        size="small"
                        label="Default ACS URL"
                        name="defaultAcsUrl"
                        value={formData.saml.defaultAcsUrl}
                        onChange={handleSamlChange}
                        fullWidth
                        required
                        helperText="Must match one of the configured ACS URLs"
                        className="global-input global-form-field"
                        InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                      />
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl size="small" fullWidth className="global-input global-form-field">
                        <InputLabel shrink sx={fieldLabelSx}>NameID User Attribute</InputLabel>
                        <Select
                          name="nameIdAttribute"
                          value={formData.saml.nameIdAttribute}
                          label="NameID User Attribute"
                          onChange={handleSamlChange}
                          MenuProps={selectMenuProps}
                          notched
                        >
                          <MenuItem value="email">User Email (default)</MenuItem>
                          <MenuItem value="username">Username</MenuItem>
                          <MenuItem value="emp_id">Employee ID</MenuItem>
                          <MenuItem value="_id">User Object ID</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Assertion Lifetime (seconds)"
                        name="assertionLifetimeSeconds"
                        type="number"
                        value={formData.saml.assertionLifetimeSeconds}
                        onChange={handleSamlChange}
                        fullWidth
                        helperText="Validity window for issued assertions (default: 300, min: 60, max: 3600)"
                        className="global-input global-form-field"
                        InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                      />
                      <TextField
                        size="small"
                        label="Max Group Claims"
                        name="maxGroupCount"
                        type="number"
                        value={formData.saml.maxGroupCount}
                        onChange={handleSamlChange}
                        fullWidth
                        helperText="Caps multi-valued group claims to prevent assertion bloat (default: 50)"
                        className="global-input global-form-field"
                        InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                      />
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.saml.signAssertion}
                            onChange={handleSamlChange}
                            name="signAssertion"
                          />
                        }
                        label="Sign Assertion"
                        sx={{ color: 'var(--vault-navy)' }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.saml.signResponse}
                            onChange={handleSamlChange}
                            name="signResponse"
                          />
                        }
                        label="Sign SAML Response"
                        sx={{ color: 'var(--vault-navy)' }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.saml.wantAuthnRequestsSigned}
                            onChange={handleSamlChange}
                            name="wantAuthnRequestsSigned"
                          />
                        }
                        label="Enforce Signed AuthnRequests"
                        sx={{ color: 'var(--vault-navy)' }}
                      />
                    </Stack>

                    <TextField
                      size="small"
                      label="SP Signing Certificate (PEM)"
                      name="spCertificate"
                      value={formData.saml.spCertificate}
                      onChange={handleSamlChange}
                      fullWidth
                      multiline
                      rows={4}
                      helperText="Public X.509 PEM certificate used to verify SP AuthnRequest signatures."
                      className="global-input global-form-field"
                      InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                    />
                  </Stack>
                )}

                {/* OIDC / STANDARD AUTHENTICATION FIELDS */}
                {formData.authenticationProtocol !== 'SAML' && (
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        size="small"
                        label="Client ID"
                        name="clientId"
                        value={formData.clientId}
                        onChange={handleChange}
                        fullWidth
                        className="global-input global-form-field"
                        InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Generate Client ID">
                                <IconButton onClick={handleGenerateClientId} edge="end" size="small" sx={{ color: 'var(--edge-blue)' }}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Client ID">
                                <IconButton onClick={() => handleCopy(formData.clientId, 'Client ID')} edge="end" size="small" sx={{ color: 'var(--edge-blue)' }}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        size="small"
                        label="Client Secret"
                        name="clientSecret"
                        type={showClientSecret ? 'text' : 'password'}
                        value={formData.clientSecret}
                        onChange={handleChange}
                        fullWidth
                        className="global-input global-form-field"
                        InputLabelProps={{ shrink: true, sx: fieldLabelSx }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Generate Client Secret">
                                <IconButton onClick={handleGenerateClientSecret} edge="end" size="small" sx={{ color: 'var(--edge-blue)' }}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Client Secret">
                                <IconButton onClick={() => handleCopy(formData.clientSecret, 'Client Secret')} edge="end" size="small" sx={{ color: 'var(--edge-blue)' }}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={showClientSecret ? 'Hide Secret' : 'Show Secret'}>
                                <IconButton onClick={() => setShowClientSecret(!showClientSecret)} edge="end" size="small" sx={{ color: 'var(--edge-blue)' }}>
                                  {showClientSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField size="small" label="Issuer" name="issuer" value={formData.issuer} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                      <TextField size="small" label="Metadata URL" name="metadataUrl" value={formData.metadataUrl} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField size="small" label="Authorization Endpoint" name="authorizationEndpoint" value={formData.authorizationEndpoint} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                      <TextField size="small" label="Token Endpoint" name="tokenEndpoint" value={formData.tokenEndpoint} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField size="small" label="User Info Endpoint" name="userInfoEndpoint" value={formData.userInfoEndpoint} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                      <TextField size="small" label="Redirect URIs" name="redirectUris" value={formData.redirectUris} onChange={handleChange} fullWidth helperText="Comma-separated values" className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField size="small" label="Logout Redirect URIs" name="logoutRedirectUris" value={formData.logoutRedirectUris} onChange={handleChange} fullWidth helperText="Comma-separated values" className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                    </Stack>
                  </Stack>
                )}
              </Box>
            )}

            {/* STEP 3: MFA POLICY */}
            {activeStep === 3 && (
              <Box>
                <Typography variant="subtitle1" className="global-h3">MFA Policy</Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                    mt: 1,
                    alignItems: 'start',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" className="global-caption" sx={{ display: 'block', mb: 1 }}>MFA Methods</Typography>
                    <Stack
                      spacing={1}
                      sx={{
                        p: 1.5,
                        borderRadius: 'var(--border-radius)',
                        border: '1px solid var(--hairline)',
                        backgroundColor: 'var(--canvas)',
                      }}
                    >
                      {mfaMethodOptions.map((method) => (
                        <FormControlLabel
                          key={method}
                          control={
                            <Radio
                              checked={selectedMfaMethod === method}
                              onChange={() => selectAllowedMfaMethod(method)}
                              sx={{ color: 'var(--slate)', '&.Mui-checked': { color: 'var(--edge-blue)' } }}
                            />
                          }
                          label={method}
                          sx={{ color: 'var(--vault-navy)', margin: 0 }}
                        />
                      ))}
                    </Stack>
                    {mfaMethodHints[selectedMfaMethod] && (
                      <Typography variant="caption" sx={{ color: 'var(--slate)', display: 'block', mt: 1 }}>
                        {mfaMethodHints[selectedMfaMethod]}
                      </Typography>
                    )}
                  </Box>

                  <FormControl size="small" fullWidth className="global-input global-form-field" sx={{ minWidth: 0 }}>
                    <InputLabel id="fallback-mfa-label" shrink sx={fieldLabelSx}>Fallback MFA Method</InputLabel>
                    <Select
                      labelId="fallback-mfa-label"
                      id="fallback-mfa-select"
                      name="defaultMfaMethod"
                      value={formData.defaultMfaMethod}
                      label="Fallback MFA Method"
                      onChange={handleChange}
                      MenuProps={selectMenuProps}
                      notched
                    >
                      <MenuItem value="">None</MenuItem>
                      {fallbackMfaMethodOptions.map((method) => (
                        <MenuItem
                          key={method}
                          value={method}
                          disabled={method === selectedMfaMethod}
                        >
                          {method}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <FormControlLabel control={<Switch checked={formData.mfaRequired} onChange={handleChange} name="mfaRequired" />} label="Require MFA" sx={{ color: 'var(--vault-navy)', mt: 1 }} />
              </Box>
            )}

            {/* STEP 4: SECURITY */}
            {activeStep === 4 && (
              <Box>
                <Typography variant="subtitle1" className="global-h3">Security</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={1}>
                  <TextField size="small" label="Allowed Domains" name="allowedDomains" value={formData.allowedDomains} onChange={handleChange} fullWidth helperText="Comma-separated values" className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Allowed IPs" name="allowedIPs" value={formData.allowedIPs} onChange={handleChange} fullWidth helperText="Comma-separated values" className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
                  <TextField size="small" label="Allowed Networks" name="allowedNetworks" value={formData.allowedNetworks} onChange={handleChange} fullWidth helperText="Comma-separated values" className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Session Timeout" name="sessionTimeoutMinutes" type="number" value={formData.sessionTimeoutMinutes} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
                  <FormControlLabel control={<Switch checked={formData.allowRememberDevice} onChange={handleChange} name="allowRememberDevice" />} label="Remember Device" sx={{ color: 'var(--vault-navy)' }} />
                  <TextField size="small" label="Remember Device Days" name="rememberDeviceDays" type="number" value={formData.rememberDeviceDays} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Max Concurrent Sessions" name="maxConcurrentSessions" type="number" value={formData.maxConcurrentSessions} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
              </Box>
            )}

            {/* STEP 5: OWNERSHIP */}
            {activeStep === 5 && (
              <Box>
                <Typography variant="subtitle1" className="global-h3">Ownership</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={1}>
                  <TextField size="small" label="Department" name="ownerDepartment" value={formData.ownerDepartment} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Owner Name" name="ownerName" value={formData.ownerName} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
                  <TextField size="small" label="Owner Email" name="ownerEmail" value={formData.ownerEmail} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                  <TextField size="small" label="Owner Phone" name="ownerPhone" value={formData.ownerPhone} onChange={handleChange} fullWidth className="global-input global-form-field" InputLabelProps={{ shrink: true, sx: fieldLabelSx }} />
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2, borderTop: '1px solid var(--hairline)' }}>
          <Button onClick={handleClose} className="global-button global-button-text">Cancel</Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((prev) => prev - 1)} className="global-button global-button-text">
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button variant="contained" onClick={handleSubmit} className="global-button global-button-primary">
              {isEditing ? 'Update' : 'Create'}
            </Button>
          ) : (
            <Button variant="contained" onClick={() => setActiveStep((prev) => prev + 1)} className="global-button global-button-primary">
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View dialog */}
      <Dialog open={openView} onClose={() => setOpenView(false)} fullWidth maxWidth="md" PaperProps={{ className: 'global-card', style: V }}>
        <DialogTitle className="global-dialog-title">Application Details</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewData && (
            <Stack spacing={2} className="global-body" sx={{ color: 'var(--slate)' }}>
              <Box>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Name:</strong> {viewData.applicationName}</Typography>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Code:</strong> {viewData.applicationCode}</Typography>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Category:</strong> {viewData.category || '—'}</Typography>
                <Typography component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                  <strong style={{ color: 'var(--vault-navy)' }}>Protocol:</strong>{' '}
                  <Chip
                    label={viewData.authenticationProtocol || 'Custom'}
                    size="small"
                    className={protocolChipClass(viewData.authenticationProtocol)}
                    sx={{ color: '#fff', fontWeight: 600, ml: 0.5 }}
                  />
                </Typography>
                <Typography>
                  <strong style={{ color: 'var(--vault-navy)' }}>Allowed MFA Method:</strong>{' '}
                  {Array.isArray(viewData.allowedMfaMethods) && viewData.allowedMfaMethods.length > 0 ? viewData.allowedMfaMethods[0] : '—'}
                </Typography>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Fallback MFA:</strong> {viewData.defaultMfaMethod || '—'}</Typography>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Status:</strong> {viewData.status || 'Active'}</Typography>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Description:</strong> {viewData.description || '—'}</Typography>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Owner:</strong> {viewData.ownerName || '—'}</Typography>
                <Typography><strong style={{ color: 'var(--vault-navy)' }}>Owner Email:</strong> {viewData.ownerEmail || '—'}</Typography>
              </Box>

              {/* SAML SERVICE PROVIDER SETUP PANEL */}
              {viewData.authenticationProtocol === 'SAML' && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 'var(--border-radius)',
                    borderColor: 'var(--hairline)',
                    backgroundColor: 'var(--mist)',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SecurityIcon sx={{ color: 'var(--edge-blue)', fontSize: 22 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--vault-navy)' }}>
                        SP Setup Values & IdP Endpoints
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadMetadata}
                      className="global-button global-button-secondary"
                    >
                      Download IdP Metadata
                    </Button>
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'var(--slate)', display: 'block', mb: 2 }}>
                    Configure these Identity Provider settings in your Service Provider application.
                  </Typography>

                  <Stack spacing={1.5}>
                    {/* IdP Entity ID */}
                    <Box sx={{ p: 1, backgroundColor: 'var(--canvas)', borderRadius: '4px', border: '1px solid var(--hairline)' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: 'var(--slate)', fontWeight: 600, display: 'block' }}>
                            IdP Entity ID (Issuer)
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--vault-navy)', wordBreak: 'break-all' }}>
                            {idpInfo?.entityId || `${getBaseOrigin()}/saml/idp`}
                          </Typography>
                        </Box>
                        <Tooltip title="Copy IdP Entity ID">
                          <IconButton size="small" onClick={() => copyToClipboard(idpInfo?.entityId || `${getBaseOrigin()}/saml/idp`, 'IdP Entity ID')}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* SSO URL Redirect */}
                    <Box sx={{ p: 1, backgroundColor: 'var(--canvas)', borderRadius: '4px', border: '1px solid var(--hairline)' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: 'var(--slate)', fontWeight: 600, display: 'block' }}>
                            SingleSignOnService URL (HTTP-Redirect Binding)
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--vault-navy)', wordBreak: 'break-all' }}>
                            {idpInfo?.ssoUrl || `${getBaseOrigin()}/saml/sso`}
                          </Typography>
                        </Box>
                        <Tooltip title="Copy SSO URL (Redirect)">
                          <IconButton size="small" onClick={() => copyToClipboard(idpInfo?.ssoUrl || `${getBaseOrigin()}/saml/sso`, 'SSO URL')}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* SSO URL POST */}
                    <Box sx={{ p: 1, backgroundColor: 'var(--canvas)', borderRadius: '4px', border: '1px solid var(--hairline)' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: 'var(--slate)', fontWeight: 600, display: 'block' }}>
                            SingleSignOnService URL (HTTP-POST Binding)
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--vault-navy)', wordBreak: 'break-all' }}>
                            {idpInfo?.ssoUrl || `${getBaseOrigin()}/saml/sso`}
                          </Typography>
                        </Box>
                        <Tooltip title="Copy SSO URL (POST)">
                          <IconButton size="small" onClick={() => copyToClipboard(idpInfo?.ssoUrl || `${getBaseOrigin()}/saml/sso`, 'SSO POST URL')}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* Metadata URL */}
                    <Box sx={{ p: 1, backgroundColor: 'var(--canvas)', borderRadius: '4px', border: '1px solid var(--hairline)' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: 'var(--slate)', fontWeight: 600, display: 'block' }}>
                            IdP Metadata URL
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--vault-navy)', wordBreak: 'break-all' }}>
                            {idpInfo?.metadataUrl || `${getBaseOrigin()}/saml/metadata`}
                          </Typography>
                        </Box>
                        <Tooltip title="Copy Metadata URL">
                          <IconButton size="small" onClick={() => copyToClipboard(idpInfo?.metadataUrl || `${getBaseOrigin()}/saml/metadata`, 'Metadata URL')}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* IdP-Initiated Launch URL */}
                    <Box sx={{ p: 1, backgroundColor: 'var(--canvas)', borderRadius: '4px', border: '1px solid var(--hairline)' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: 'var(--slate)', fontWeight: 600, display: 'block' }}>
                            IdP-Initiated SSO Launch URL (Direct Login)
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--vault-navy)', wordBreak: 'break-all' }}>
                            {`${getBaseOrigin()}/saml/idp-initiated?appId=${viewData._id || viewData.id}`}
                          </Typography>
                        </Box>
                        <Tooltip title="Copy IdP-Initiated Launch URL">
                          <IconButton size="small" onClick={() => copyToClipboard(`${getBaseOrigin()}/saml/idp-initiated?appId=${viewData._id || viewData.id}`, 'IdP-Initiated URL')}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* Active & Next Certificates */}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--vault-navy)' }}>
                      IdP Signing Certificates
                    </Typography>

                    {activeCert && (
                      <Box sx={{ p: 1, backgroundColor: 'var(--canvas)', borderRadius: '4px', border: '1px solid var(--hairline)' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label="Active Signing Certificate" size="small" className="global-chip signal-secure-bg" sx={{ color: '#fff', fontSize: 11 }} />
                              <Typography variant="caption" sx={{ color: 'var(--slate)' }}>
                                Key ID: {activeCert.keyId}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--vault-navy)', mt: 0.5, wordBreak: 'break-all' }}>
                              Fingerprint: {activeCert.fingerprint}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--slate)' }}>
                              Expires: {activeCert.validTo ? new Date(activeCert.validTo).toUTCString() : '—'}
                            </Typography>
                          </Box>
                          <Tooltip title="Copy Fingerprint">
                            <IconButton size="small" onClick={() => copyToClipboard(activeCert.fingerprint, 'Active Certificate Fingerprint')}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    )}

                    {nextCert && (
                      <Box sx={{ p: 1, backgroundColor: 'var(--canvas)', borderRadius: '4px', border: '1px solid var(--hairline)' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label="Staged Next Certificate" size="small" className="global-chip signal-elevated-bg" sx={{ color: '#fff', fontSize: 11 }} />
                              <Typography variant="caption" sx={{ color: 'var(--slate)' }}>
                                Key ID: {nextCert.keyId}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--vault-navy)', mt: 0.5, wordBreak: 'break-all' }}>
                              Fingerprint: {nextCert.fingerprint}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--slate)' }}>
                              Expires: {nextCert.validTo ? new Date(nextCert.validTo).toUTCString() : '—'}
                            </Typography>
                          </Box>
                          <Tooltip title="Copy Fingerprint">
                            <IconButton size="small" onClick={() => copyToClipboard(nextCert.fingerprint, 'Next Certificate Fingerprint')}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    )}

                    {/* Certificate Management (SuperAdmin only) */}
                    {isSuperAdmin && (
                      <Box mt={1}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<KeyIcon />}
                            onClick={() => setStageConfirmOpen(true)}
                            className="global-button global-button-secondary"
                          >
                            Stage New Certificate
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!nextCert}
                            onClick={() => setPromoteConfirmOpen(true)}
                            className="global-button global-button-primary"
                          >
                            Promote Staged Certificate
                          </Button>
                        </Stack>
                        {!nextCert && (
                          <Typography variant="caption" sx={{ color: 'var(--slate)', display: 'block', mt: 0.5 }}>
                            Certificate promotion is enabled only after a 'next' certificate has been staged.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid var(--hairline)' }}>
          <Button onClick={() => setOpenView(false)} className="global-button global-button-text">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onClose={handleCancelDelete} PaperProps={{ className: 'global-card', style: V }}>
        <DialogTitle className="global-dialog-title">Delete Application</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--vault-navy)', mt: 1 }}>
            Are you sure you want to delete{' '}
            <strong className="global-text-critical">{deletingApplication?.applicationName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} className="global-button global-button-text">Cancel</Button>
          <Button variant="contained" onClick={handleConfirmDelete} className="global-button-danger">
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Certificate Staging Confirmation Dialog (SuperAdmin only) */}
      <Dialog open={stageConfirmOpen} onClose={() => setStageConfirmOpen(false)} PaperProps={{ className: 'global-card', style: V }}>
        <DialogTitle className="global-dialog-title">Stage New Signing Certificate</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--vault-navy)', mt: 1 }}>
            Staging a new certificate will generate and dual-publish a <strong>'next'</strong> signing certificate in the IdP metadata.
          </Typography>
          <Typography sx={{ color: 'var(--slate)', mt: 1 }}>
            The current active certificate will continue to sign all SAML assertions. Both certificates will be published in IdP metadata so
            Service Providers can add the new certificate before it is promoted.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Do not promote the certificate until all connected Service Providers have updated their configuration.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageConfirmOpen(false)} className="global-button global-button-text">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStageCert}
            disabled={certActionLoading}
            className="global-button global-button-primary"
          >
            {certActionLoading ? <CircularProgress size={20} color="inherit" /> : 'Stage Certificate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Certificate Promotion Confirmation Dialog (SuperAdmin only) */}
      <Dialog open={promoteConfirmOpen} onClose={() => setPromoteConfirmOpen(false)} PaperProps={{ className: 'global-card', style: V }}>
        <DialogTitle className="global-dialog-title">Promote Staged Certificate to Active</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--vault-navy)', mt: 1 }}>
            Promoting the staged certificate will immediately switch active SAML assertion signing to the new certificate and retire the previous active certificate.
          </Typography>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
            <strong>CRITICAL WARNING:</strong> Every connected Service Provider MUST trust the new certificate before promotion.
            Any Service Provider that has not installed the new certificate will immediately fail authentication upon promotion.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteConfirmOpen(false)} className="global-button global-button-text">Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePromoteCert}
            disabled={certActionLoading}
            className="global-button global-button-primary"
          >
            {certActionLoading ? <CircularProgress size={20} color="inherit" /> : 'Promote to Active'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          onClose={handleSnackClose}
          severity={snackSeverity}
          className="global-alert"
          style={V}
          sx={{ width: '100%', backgroundColor: 'var(--canvas)', color: 'var(--vault-navy)', border: '1px solid var(--hairline)' }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}